import React, { useEffect, useState } from 'react'
import {
  Form,
  Row,
  Col,
  Select,
  Upload,
  Button,
  Divider,
  Space,
  Table,
  Input,
  Modal,
  Spin,
} from 'antd'
import {
  UploadOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import {
  DeleteEducationalDocuments,
  getChildrenList,
  GetEducationalDocuments,
  GetEducationalDocumentsById,
  IMG_URL,
  saveEducationalDocuments,
  UpdateEducationalDocuments,
} from '../APIComponents/AdminApi'
import AdminFooter from '../CommonComponents/AdminFooter'
import AdminHeader from '../CommonComponents/AdminHeader'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const EducationalDocumentsForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_view: 1,
        is_edit: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find(
        (p) => p.page_name === 'Educational Documents',
      )
      if (page) return page
    }

    return {
      is_new: 0,
      is_view: 0,
      is_edit: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)

  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)

  const handleView = (record) => {
    setViewData(record)
    setViewModalVisible(true)
  }

  useEffect(() => {
    fetchChildren()
  }, [])

  useEffect(() => {
    if (childrenList.length > 0) {
      fetchTableData()
    }
  }, [childrenList])

  // ================= Fetch Children =================
  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data)
    } catch {
      toast.error('Failed to fetch children list')
    }
  }

  // ================= Fetch Table Data =================
  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetEducationalDocuments)
      const docs = res.data || []

      // Merge with children list
      const merged = docs.map((doc) => {
        const child = childrenList.find((c) => c.id === doc.reg_id)

        return {
          ...doc,
          student: child || {},
        }
      })

      setTableData(merged)
    } catch {
      toast.error('Failed to fetch table data')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= ADD =================
  const handleAdd = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(true)
  }

  // ================= EDIT =================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)
      const res = await axiosInstance.get(
        `${GetEducationalDocumentsById}/${record.reg_id}`,
      )

      const docs = res.data

      form.setFieldsValue({
        childName: record.reg_id,

        schoolIdCard: docs.school_id_card
          ? [
              {
                uid: '-1',
                name: docs.school_id_card,
                status: 'done',
                url: IMG_URL + docs.school_id_card,
              },
            ]
          : [],

        previousYearMarksheet: docs.previous_year_marksheet
          ? [
              {
                uid: '-2',
                name: docs.previous_year_marksheet,
                status: 'done',
                url: IMG_URL + docs.previous_year_marksheet,
              },
            ]
          : [],

        transferCertificate: docs.transfer_certificate
          ? [
              {
                uid: '-3',
                name: docs.transfer_certificate,
                status: 'done',
                url: IMG_URL + docs.transfer_certificate,
              },
            ]
          : [],

        bonafideCertificate: docs.bonafide_certificate
          ? [
              {
                uid: '-4',
                name: docs.bonafide_certificate,
                status: 'done',
                url: IMG_URL + docs.bonafide_certificate,
              },
            ]
          : [],

        specialAchievementCertificates: docs.special_achievement_certificates
          ? [
              {
                uid: '-5',
                name: docs.special_achievement_certificates,
                status: 'done',
                url: IMG_URL + docs.special_achievement_certificates,
              },
            ]
          : [],
      })

      setIsEdit(true)
      setRecordId(docs.reg_id)
      setShowForm(true)
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteEducationalDocuments}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchTableData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // ================= SUBMIT / UPDATE =================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()
      if (!isEdit) {
        formData.append('reg_id', values.childName)
      }
      const appendFile = (field, key) => {
        if (!values[field] || values[field].length === 0) return

        const file = values[field][0]

        if (file.originFileObj) {
          formData.append(key, file.originFileObj)
        } else if (file.url) {
          formData.append(key, file.url.replace(IMG_URL, ''))
        }
      }

      appendFile('schoolIdCard', 'school_id_card')
      appendFile('previousYearMarksheet', 'previous_year_marksheet')
      appendFile('transferCertificate', 'transfer_certificate')
      appendFile('bonafideCertificate', 'bonafide_certificate')
      appendFile(
        'specialAchievementCertificates',
        'special_achievement_certificates',
      )

      let res

      if (isEdit) {
        res = await axiosInstance.put(
          `${UpdateEducationalDocuments}/${recordId}`,
          formData,
        )

        if (res.data === 'UPDATED') {
          toast.success('Updated Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        }
      } else {
        res = await axiosInstance.post(saveEducationalDocuments, formData)

        if (res.data === 'SAVED') {
          toast.success('Saved Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        } else if (res.data === 'ALREADY_EXIST') {
                  toast.warn('Data already exist!')
                }
      }
    } catch {
      toast.error('Failed to submit data')
    } finally {
      setLoading(false)
    }
  }

  // ================= File Validation =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Reg No',
      render: (_, record) => record?.student?.reg_no || '',
    },
    {
      title: 'Student Name',
      render: (_, record) =>
        `${record?.student?.fname || ''} 
       ${record?.student?.mname || ''} 
       ${record?.student?.lname || ''}`,
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              Educational Documents
            </h3>
          </div>

          {/* ================= TABLE ================= */}
          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>

                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={tableData.filter((item) => {
                  const search = searchText.toLowerCase()

                  return (
                    item?.student?.fname?.toLowerCase().includes(search) ||
                    item?.student?.mname?.toLowerCase().includes(search) ||
                    item?.student?.lname?.toLowerCase().includes(search) ||
                    item?.student?.reg_no?.toLowerCase().includes(search)
                  )
                })}
                columns={columns}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {/* ================= FORM ================= */}
          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container rounded shadow">
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                  <Row gutter={16} className="mt-3 p-2 border rounded">
                    <Col xs={24} md={8}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          disabled={isEdit}
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          placeholder="Search by name / reg no"
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16} className="p-2 border rounded mt-3">
                    {[
                      { label: 'School ID Card', name: 'schoolIdCard' },
                      {
                        label: 'Previous Year Marksheet',
                        name: 'previousYearMarksheet',
                      },
                      {
                        label: 'Transfer Certificate',
                        name: 'transferCertificate',
                      },
                      {
                        label: 'Bonafide Certificate',
                        name: 'bonafideCertificate',
                      },
                      {
                        label: 'Special Achievement Certificates',
                        name: 'specialAchievementCertificates',
                      },
                    ].map((item) => (
                      <Col xs={24} md={12} lg={6} key={item.name}>
                        <Form.Item
                          label={item.label}
                          name={item.name}
                          valuePropName="fileList"
                          getValueFromEvent={(e) =>
                            Array.isArray(e) ? e : e?.fileList
                          }
                          rules={[{ required: true }]}
                        >
                          <Upload
                            beforeUpload={validateFile}
                            accept=".jpg,.jpeg,.png,.pdf"
                            maxCount={1}
                            listType="picture"
                            style={{ width: '100%' }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: '100%' }}
                            >
                              Upload (Max 3MB)
                            </Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>

                  <Divider />

                  <Row justify="end" className="pb-5">
                    <Space>
                      <Button
                        onClick={() => {
                          form.resetFields()
                          setIsEdit(false)
                          setRecordId(null)
                          setShowForm(false)
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Educational Documents
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20, // space from top
              paddingBottom: 50, // space from bottom
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh', // control height
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 5px',
                  borderRadius: '8px',
                }}
              >
                {/* Reg No */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Registration No :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.student?.reg_no || '-'}
                    </div>
                  </Col>
                </Row>

                {/* Student Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData?.student?.fname || ''}{' '}
                      {viewData?.student?.mname || ''}{' '}
                      {viewData?.student?.lname || ''}
                    </div>
                  </Col>
                </Row>

                <Divider />

                <Row gutter={16}>
                  {viewData.school_id_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>School ID Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.school_id_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.previous_year_marksheet && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Previous Year Marksheet</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.previous_year_marksheet}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.transfer_certificate && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Transfer Certificate</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.transfer_certificate}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.bonafide_certificate && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Bonafide Certificate</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.bonafide_certificate}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.special_achievement_certificates && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Special Achievement Certificates</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={
                              IMG_URL +
                              viewData.special_achievement_certificates
                            }
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default EducationalDocumentsForm
